import { BuildInEmail } from '@flowus/common/const';
import { cx } from '@flowus/common/cx';
import { atom, useAtom } from 'jotai';
import type { FC } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Divider } from 'src/common/components/divider';
import type { IconName } from 'src/common/components/icon';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { SPECIAL_SPACE_ID, publicLink } from 'src/common/const';
import { request } from 'src/common/request';
import { useNewUserTask } from 'src/hooks/activities/use-activity';
import { useHasNewActivity } from 'src/hooks/activities/use-has-new-activity';
import { useGetUnreadCount } from 'src/hooks/public/use-get-unread-count';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { updateActivitiesState } from 'src/services/activities/hook';
import { setAppUiState } from 'src/services/app';
import { useUserBalance } from 'src/services/user/user-balance';
import { bizTracker } from 'src/utils/biz-tracker';
import { createAnchorDomClick } from 'src/utils/dom';
import { openQiMoor } from 'src/utils/qi-moor';
import { useSetDrawerOpenState } from 'src/views/main/drawer/shared';
import { OpenSettingFrom, SettingMenuType } from 'src/views/main/setting-modal/type';
import { useOpenSettingModal } from 'src/views/main/setting-modal/use-open-setting-modal';
import { Footer } from './footer';
import { MessageBoard, useMessageBoardList } from './message-board';
import { MessageCard } from './message-card';
import { RecommendList } from './recomend-list';
import { StrateGuides } from './strategy-guides';
import { TaskListPanel } from './task-list';
import { MessageBoardType } from './type';

enum ViewType {
  MainBoard,
  StrategyBoard,
  MessageBoard,
}

const $viewType = atom(ViewType.MainBoard);

export const ServicePanel: FC = () => {
  const setDrawerState = useSetDrawerOpenState();

  return (
    <div className="h-full flex flex-col w-full">
      <div className={'flex items-center py-1 px-2 space-x-2 border-b flex-shrink-0'}>
        <Tooltip
          className="flex items-center justify-center p-1 text-grey3 animate-hover"
          onClick={() => setDrawerState(false)}
        >
          <Icon name="IcUnfold" size="middle" />
        </Tooltip>
        <ServicePanelNormalHeader />
      </div>
      <ServicePanelNormal />
      <Footer />
    </div>
  );
};

const ServicePanelNormal: FC = () => {
  const { integralInfo } = useUserBalance();
  const openSettingModal = useOpenSettingModal();
  const openModal = useOpenModal();
  const messageBoardList = useMessageBoardList();
  const { progress } = useNewUserTask();
  const [unreadCount, setUnreadCount] = useState({ strategyGuides: 0, messageBoard: 0 });

  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewType, setViewType] = useAtom($viewType);
  // const openAskUpgradeSpace = useOpenAskUpgradeSpace();
  // const openUpgradeSpace = useOpenUpgradeSpace();
  const hasNewActivity = useHasNewActivity();

  const fetchActivityList = useCallback(async () => {
    const res = await request.infra.getActivityList('all', 'all', getCurrentSpaceId());
    updateActivitiesState(res);
  }, []);

  useEffect(() => {
    void fetchActivityList();
  }, [fetchActivityList]);
  const getUnreadCount = useGetUnreadCount();

  useEffect(() => {
    void getUnreadCount().then(({ strategyGuides, messageBoard }) => {
      setUnreadCount({ strategyGuides, messageBoard }); // 逻辑有点怪，信息板点开黄豆豆即视为已读

      if (messageBoard !== 0) {
        void request.editor
          .setNotificationRead({
            spaceId: SPECIAL_SPACE_ID,
            channel: 'messageBoard',
            timestamp: Date.now(),
          })
          .then(() => {
            if (strategyGuides === 0) {
              setAppUiState({ $serviceCenterRedBadge: false });
            }
          });
      }
    });
  }, [getUnreadCount]);

  const setNotificationReadEd = (channel: string) => {
    void request.editor
      .setNotificationRead({
        spaceId: SPECIAL_SPACE_ID,
        channel,
        timestamp: Date.now(),
      })
      .then(() => {
        setUnreadCount((unreadCount) => {
          const result = { ...unreadCount, [channel]: 0 };
          if (result.strategyGuides === 0) {
            setAppUiState({ $serviceCenterRedBadge: false });
          }
          return result;
        });
      });
  };

  // 打开使用秘笈
  const openStrategyBoard = () => {
    if (unreadCount.strategyGuides > 0) {
      setNotificationReadEd('strategyGuides');
    }
    setViewType(ViewType.StrategyBoard);
  };

  const list1 = [
    {
      icon: 'IcMenuHelp',
      title: '帮助中心',
      onClick: () => {
        bizTracker.event('help_centre');
        window.open(publicLink.helpCenter);
      },
    },
    {
      icon: 'IcMenuMessage',
      hidden: __BUILD_IN__ || __PRIVATE__,
      title: '问题反馈',
      onClick: () => window.open(publicLink.problemFeedback),
    },
    {
      icon: 'IcMenuFeedback',
      title: '在线客服',
      hidden: __PRIVATE__,
      onClick: () => {
        if (__BUILD_IN__) {
          createAnchorDomClick(`mailto:${BuildInEmail}`);
        } else {
          openQiMoor(openModal);
        }
      },
    },
    {
      icon: 'IcMenuUse',
      title: '使用秘籍',
      hidden: __BUILD_IN__ || __PRIVATE__,
      tip: unreadCount.strategyGuides > 0,
      onClick: openStrategyBoard,
    },
    {
      icon: 'MIcSettingsInvite',
      title: '活动中心',
      hidden: __BUILD_IN__ || __PRIVATE__,
      onClick: () =>
        openSettingModal({
          defaultMenuType: SettingMenuType.integral,
          from: OpenSettingFrom.package,
        }),
      rightText: integralInfo?.todayScore ? (
        <span className="flex items-center absolute right-0 -top-2 text-t4 px-1.5 text-white bg-red rounded-sm ">
          +{integralInfo.todayScore} 积分
        </span>
      ) : hasNewActivity ? (
        <span className="flex items-center absolute -right-1 -top-1 text-t4-bold px-1.5 text-red rounded-sm">
          NEW
        </span>
      ) : null,
    },
  ];

  const list2 = [
    {
      title: '快捷键',
      onClick: () => {
        bizTracker.event('keyboard_shortcut');
        window.open(publicLink.shortcut);
      },
    },
    {
      title: '快捷命令',
      onClick: () => {
        bizTracker.event('slash_shortcut');
        window.open(publicLink.shortcutCommand);
      },
    },
    {
      title: '更新日志',
      hidden: __PRIVATE__,
      onClick: () => {
        bizTracker.event('update_news');
        window.open(publicLink.updateLog);
      },
    },
  ].filter((i) => !i.hidden);

  const unreadMessage = messageBoardList.find((item) => {
    if (!item.read) return true;

    const type = item.edits?.[0]?.messageBoardProperty?.type as MessageBoardType;

    if (type === MessageBoardType.NEW_USER_TASK && progress !== 1) {
      return true;
    }

    return false;
  });

  const messageType = unreadMessage?.edits?.[0]?.messageBoardProperty?.type as MessageBoardType;

  return (
    <div className="h-full flex flex-col w-full overflow-y-auto">
      {viewType === ViewType.MessageBoard && <MessageBoard />}
      {viewType === ViewType.StrategyBoard && <StrateGuides />}
      <div
        className={cx('h-full w-full flex flex-col justify-end overflow-hidden', {
          hidden: viewType !== ViewType.MainBoard, // hidden为了减少组件重新创建引起请求重复
        })}
      >
        <div className="overflow-y-auto px-4 pb-4 h-full">
          <div className="relative mt-3 mb-2.5">
            <Input
              inputRef={inputRef}
              autoFocus
              className="h-8 pl-10 rounded"
              inputClassName="pl-0"
              placeholder="输入功能关键词，搜索相关帮助文档"
              value={keyword}
              onChange={setKeyword}
            />
            <Icon
              size="normal"
              className="absolute left-3 top-1.5 text-xl text-grey4 leading-none"
              name="IcSearch"
            />
            {keyword && (
              <Icon
                size="normal"
                onClick={() => {
                  setKeyword('');
                  inputRef.current?.focus();
                }}
                name={'IcUploadCancel'}
                className="absolute right-0 top-2 text-grey4 mr-2"
                style={{ width: '18px', height: '18px' }}
              />
            )}
          </div>

          {!keyword && messageType && (
            <MessageCard
              overlap={true}
              onCloseModal={() => {
                // do nothing?
              }}
              messageType={messageType}
            />
          )}
          <RecommendList keyword={keyword} />
          <TaskListPanel />
        </div>
        {!keyword && (
          <div className="flex flex-col border-t">
            <div className="flex flex-wrap px-4">
              {list2.map((item) => {
                return (
                  <div
                    className={cx(
                      'flex items-center h-[40px] text-t3 p-2 animate-hover',
                      __BUILD_IN__ ? 'basis-[50%]' : 'basis-[33%]'
                    )}
                    key={item.title}
                    onClick={item.onClick}
                  >
                    {item.title}
                  </div>
                );
              })}
            </div>

            <Divider className="my-1" />
            <div className="flex flex-wrap px-4 pb-1">
              {list1.map((item) => {
                if (item.hidden) return null;
                return (
                  <div
                    className={cx(
                      'relative flex items-center animate-hover',
                      __BUILD_IN__ ? 'basis-[50%]' : 'basis-[33%]'
                    )}
                    key={item.title}
                  >
                    <div
                      className="relative cursor-pointer flex items-center text-t3 py-2 pl-1 w-full"
                      onClick={item.onClick}
                    >
                      {item.tip && (
                        <div className="absolute left-[26px] top-1 w-2.5 h-2.5 bg-red rounded-full" />
                      )}
                      <div className="px-1 mr-1.5 flex items-center bg-grey8 rounded">
                        <Icon name={item.icon as IconName} size="middle" />
                      </div>
                      {item.title}
                      {item.rightText}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ServicePanelNormalHeader: FC = () => {
  const [viewType, setViewType] = useAtom($viewType);

  return (
    <div className="flex items-center justify-end flex-1" hidden={__BUILD_IN__}>
      {viewType === ViewType.MainBoard && (
        <>
          <button
            className="animate-hover flex items-center text-grey3 space-x-1 px-1"
            onClick={() => {
              setViewType(ViewType.MessageBoard);
            }}
          >
            <span className="text-t2">最近信息板</span>
            <Icon name="IcArrowForward" size="middle" />
          </button>
        </>
      )}
      {viewType !== ViewType.MainBoard && (
        <>
          <button
            className="animate-hover flex items-center text-grey3 space-x-1 px-1"
            onClick={() => {
              setViewType(ViewType.MainBoard);
            }}
          >
            <span className="text-t2">
              {viewType === ViewType.StrategyBoard ? '使用秘籍' : '最近信息板'}
            </span>
            <Icon name="IcArrowBack" size="middle" />
          </button>
        </>
      )}
    </div>
  );
};
